export abstract class BaseError<TErrorCode> extends Error {
  protected state: Record<string, string> = {};

  displayName: string;
  prefix: string;
  errorMessage: string;

  parent: Error | BaseError<TErrorCode> | undefined;

  messages: string[][] = [];

  public code: TErrorCode;

  protected constructor(_message: string, _code: TErrorCode, _prefix: string) {
    const message = _message ? ` ${_message}` : '';
    const displayName = `[${_prefix}]#${_code}`;

    super(`${displayName}.${message}`);

    this.displayName = displayName;
    this.errorMessage = _message;
    this.prefix = _prefix;
    this.code = _code;

    this.name = displayName;

    Object.defineProperty(this, 'message', {
      get: () => {
        return this.getDisplayMessage();
      },
    });

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, BaseError);
    }
  }

  get parentError(): BaseError<any> | null {
    const parent = this.parent;

    if (!parent) {
      return null;
    }

    if (this.parent instanceof BaseError) {
      return this.parent;
    }

    const displayName = parent.name ?? '[Unknown Error]';

    return {
      getBreadcrumbs() {
        return [displayName];
      },
      getMessage() {
        return [displayName, parent.message ?? 'unknown error message'].join(
          '\n',
        );
      },
    } as unknown as BaseError<any>;
  }

  /**
   * Returns formatted error message with breadcrumbs
   */
  getDisplayMessage() {
    const breadcrumbs = this.parentError ? this.getBreadcrumbs() : [];

    return [breadcrumbs.join(' -> '), this.getMessage()]
      .filter((x) => x.trim().length)
      .join('\n');
  }

  /**
   * Returns formatted error message
   */
  getMessage() {
    const state = Object.entries(this.state)
      .reduce((acc, [key, value]) => {
        if (value) {
          acc.push(` – ${key}: ${value}`);
        }
        return acc;
      }, [] as string[])
      .join('\n');

    const messages = this.messages
      .reduce((acc, _messages) => {
        acc.push(` - ${_messages.join(' ')}`);

        return acc;
      }, [] as string[])
      .join('\n');

    const parentError = this.parentError;

    const parentErrorMessage: string = parentError
      ? ['', parentError.getMessage(), ''].join('\n')
      : '';

    const thrownErrorMessage = this.errorMessage
      ? `${this.displayName}: ${this.errorMessage}`
      : this.displayName;

    return [parentErrorMessage, thrownErrorMessage, state, messages]
      .filter((x) => x.trim().length)
      .join('\n');
  }

  getBreadcrumbs(): string[] {
    const parentError = this.parentError;

    if (!parentError) {
      return [this.displayName];
    }

    return [...parentError.getBreadcrumbs(), this.displayName];
  }

  public withParentError(e: Error | BaseError<any>) {
    this.parent = e;
    return this;
  }

  public withMessage(...messages: string[]) {
    this.messages.push(messages);
    return this;
  }
}

export function createErrorBuilder<TCode, TFactory extends BaseError<TCode>>(
  factory: new (...args: any) => TFactory,
) {
  return function throwException(code: TCode, message = '') {
    const instance = new factory(message, code);

    if (Error.captureStackTrace) {
      Error.captureStackTrace(instance, throwException);
    }

    return instance;
  };
}
